import { useState } from 'react'
import Modal from './Modal'

/**
 * Confirmation for a destructive action.
 *
 * Firestore has no undo, so nothing is deleted on a single click anywhere in
 * the app. The error is shown in place rather than closing on failure — a
 * dialog that vanishes after a permission error looks exactly like success.
 */
export default function DeleteConfirm({
  label,
  detail,
  onConfirm,
  onCancel,
}: {
  label: string
  detail?: string
  onConfirm: () => Promise<void>
  onCancel: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  return (
    <Modal
      open
      title="Delete"
      width={420}
      onClose={onCancel}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onCancel} disabled={busy}>
            Keep it
          </button>
          <button
            className="btn btn-danger"
            disabled={busy}
            onClick={async () => {
              setBusy(true)
              setError(null)
              try {
                await onConfirm()
              } catch (caught) {
                setError((caught as Error).message)
              } finally {
                setBusy(false)
              }
            }}
          >
            {busy ? 'Deleting…' : 'Delete'}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="t-body">
          Delete <strong>{label}</strong>?
        </p>
        {detail && <p className="t-caption">{detail}</p>}
        {error && (
          <p role="alert" className="t-caption" style={{ color: 'var(--destructive)' }}>
            {error}
          </p>
        )}
      </div>
    </Modal>
  )
}
